import PropTypes from "prop-types";

/**
 * RealisticConveyer – High-fidelity 3D industrial conveyor belt component.
 * Features:
 * - Speed scales dynamically with live value (RPM / m/s)
 * - Full-length cargo traveling from start (left) across the entire belt body to the right end (translateX 390px)
 * - Animated rubber belt chevron texture
 * - Motor status pilot LED
 */
const RealisticConveyer = ({
  liveValue,
  isRunning,
  isOffline,
  statusColor,
  speedScale = 1,
  ratio = 1,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

  // Dynamic speed based strictly on percentage speedScale
  const effectiveSpeed = Math.max(0.3, speedScale || 1);
  const beltDuration = isRunning && !isOffline ? `${Math.max(0.3, 1.2 / effectiveSpeed)}s` : "0s";
  const cargoDuration = isRunning && !isOffline ? `${Math.max(0.8, 3.2 / effectiveSpeed)}s` : "0s";

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/Conveyor.png"
        alt="SCADA Realistic Conveyor"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 518x228 space) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 518 228"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <clipPath id="conveyorBeltClip">
            <polygon points="65,6 487,6 502,29 72,29" />
          </clipPath>
          <filter id="conveyorLedGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* ── Moving Rubber Belt Surface Texture ── */}
        {isRunning && !isOffline && (
          <g clipPath="url(#conveyorBeltClip)">
            <g style={{ animation: `scada-belt-scroll ${beltDuration} linear infinite` }}>
              {[...Array(28)].map((_, i) => (
                <line
                  key={i}
                  x1={i * 24 - 48}
                  y1="6"
                  x2={i * 24 - 34}
                  y2="29"
                  stroke="#334155"
                  strokeWidth="2.5"
                  opacity="0.65"
                />
              ))}
            </g>
          </g>
        )}

        {/* ── Traveling Industrial Cargo Boxes (Full Belt Length Travel) ── */}
        {isRunning && !isOffline && (
          <g>
            {/* Box 1 (Amber) */}
            <g style={{ animation: `scada-cargo-travel ${cargoDuration} linear infinite` }}>
              <rect x="75" y="8" width="18" height="14" rx="2" fill="#d97706" opacity="0.95" />
              <line x1="84" y1="8" x2="84" y2="22" stroke="#b45309" strokeWidth="1" />
            </g>

            {/* Box 2 (Blue) */}
            <g
              style={{
                animation: `scada-cargo-travel ${cargoDuration} linear infinite`,
                animationDelay: `calc(${cargoDuration} * 0.5)`,
              }}
            >
              <rect x="75" y="8" width="22" height="15" rx="2" fill="#0284c7" opacity="0.95" />
              <line x1="86" y1="8" x2="86" y2="23" stroke="#0369a1" strokeWidth="1" />
            </g>
          </g>
        )}

        {/* ── Left Motor Status LED ── */}
        <circle
          cx="37"
          cy="89"
          r="4.5"
          fill={dotColor}
          stroke="#ffffff"
          strokeWidth="1.2"
          filter={isRunning ? "url(#conveyorLedGlow)" : "none"}
          style={{
            animation: isRunning ? "scada-led-blink 2s infinite ease-in-out" : "none",
          }}
        />
      </svg>
    </div>
  );
};

RealisticConveyer.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  speedScale: PropTypes.number,
  opacity: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RealisticConveyer;
